import type { Camera } from '@babylonjs/core/Cameras/camera';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';

import type { GameStore, ScreenAnchor } from '../state/store';

/** How far above an entity's feet its HUD elements hang, in world units. */
const ANCHOR_HEIGHT = 2.4;

/** How high above a dropped item its label floats. */
/**
 * How high a zone landmark's icon floats.
 *
 * Above head height on purpose: the portal and the exit are things to walk *to*,
 * so their marker has to stay readable with a character standing on it.
 */
const MARKER_ANCHOR_HEIGHT = 3.4;

const LOOT_ANCHOR_HEIGHT = 1.6;

/**
 * How high a workshop's label floats.
 *
 * Lower than a landmark's 3.4: an artisan is 1.84 tall and the stall's sign is lower
 * still, so a label at landmark height would hang in the air a metre above the shop it
 * names. This clears the figure's head and no more.
 */
const STATION_ANCHOR_HEIGHT = 2.6;

/**
 * Projects entity positions to screen space so the Solid HUD can pin health bars
 * and damage numbers to them.
 *
 * Runs once per frame and pushes a single object into one signal. That is a
 * deliberate trade: one reactive write per frame for the whole HUD, rather than
 * one per element — which is what would actually cost something.
 */
export class ScreenProjector {
  private readonly scratch = new Vector3();
  private readonly projected = new Vector3();
  private readonly viewport = { width: 0, height: 0 };

  constructor(
    private readonly scene: Scene,
    private readonly camera: Camera,
    private readonly store: GameStore,
  ) {}

  /**
   * `selfPosition` is the local player's predicted position. Without it the
   * player's own anchors would come from the snapshot and trail their own mesh by
   * up to a tick, which is visible as the health bar sliding behind the body.
   */
  update(selfPosition?: { x: number; z: number }): void {
    const engine = this.scene.getEngine();
    this.viewport.width = engine.getRenderWidth();
    this.viewport.height = engine.getRenderHeight();

    const transform = this.scene.getTransformMatrix();
    const viewport = this.camera.viewport.toGlobal(this.viewport.width, this.viewport.height);

    // A fresh object per frame is the price of handing the HUD an immutable
    // value; the alternative is mutating state Solid is reading mid-render.
    const anchors: Record<string, ScreenAnchor> = {};

    // Ground loot gets an anchor too, so the pickup prompt can follow it. Lower
    // than an entity's, because a drop sits on the floor.
    for (const g of this.store.ground()) {
      this.scratch.set(g.x, LOOT_ANCHOR_HEIGHT, g.z);
      Vector3.ProjectToRef(this.scratch, Matrix.IdentityReadOnly, transform, viewport, this.projected);
      anchors[g.id] = {
        x: this.projected.x,
        y: this.projected.y,
        visible: this.projected.z > 0 && this.projected.z < 1,
      };
    }

    // An open town portal is a landmark too, at the same height as the entry and the
    // exit — it is a thing to walk to, so its label has to clear a character standing
    // on it. Its anchor is keyed by the portal's own id rather than a fixed name,
    // because unlike the other two there may be none of it.
    for (const portal of this.store.portals()) {
      this.scratch.set(portal.x, MARKER_ANCHOR_HEIGHT, portal.z);
      Vector3.ProjectToRef(this.scratch, Matrix.IdentityReadOnly, transform, viewport, this.projected);
      anchors[portal.id] = {
        x: this.projected.x,
        y: this.projected.y,
        visible: this.projected.z > 0 && this.projected.z < 1,
      };
    }

    // The zone's own landmarks. Two fixed points rather than entities, so they get
    // ids of their own — `WorldMarkers` looks them up by these names.
    const zone = this.store.zone();

    // And the town's workshops, which are the same kind of thing: fixed places worth
    // walking to, anchored off the ZONE frame rather than off anything per-frame. Lower
    // than the landmarks, because an artisan is 1.84 tall and a label at 3.4 would float
    // clear of them; this sits just over their head.
    for (const station of this.store.stations()) {
      this.scratch.set(station.at.x, STATION_ANCHOR_HEIGHT, station.at.z);
      Vector3.ProjectToRef(this.scratch, Matrix.IdentityReadOnly, transform, viewport, this.projected);
      anchors[`station-${station.kind}`] = {
        x: this.projected.x,
        y: this.projected.y,
        visible: this.projected.z > 0 && this.projected.z < 1,
      };
    }

    if (zone) {
      for (const [id, at] of [
        ['zone-entry', zone.spawn],
        ['zone-exit', zone.exit],
      ] as const) {
        this.scratch.set(at.x, MARKER_ANCHOR_HEIGHT, at.z);
        Vector3.ProjectToRef(
          this.scratch,
          Matrix.IdentityReadOnly,
          transform,
          viewport,
          this.projected,
        );
        anchors[id] = {
          x: this.projected.x,
          y: this.projected.y,
          visible: this.projected.z > 0 && this.projected.z < 1,
        };
      }
    }

    const selfId = this.store.selfId();

    for (const [id, snap] of this.store.raw) {
      const usePredicted = selfPosition !== undefined && id === selfId;
      this.scratch.set(
        usePredicted ? selfPosition.x : snap.x,
        ANCHOR_HEIGHT,
        usePredicted ? selfPosition.z : snap.z,
      );
      Vector3.ProjectToRef(this.scratch, Matrix.IdentityReadOnly, transform, viewport, this.projected);

      anchors[id] = {
        x: this.projected.x,
        y: this.projected.y,
        // z outside [0,1] means the point is behind the camera or past the far
        // plane, where a projected coordinate is meaningless.
        visible: this.projected.z > 0 && this.projected.z < 1,
      };
    }

    this.store.setAnchors(anchors);
    // The HUD's clock, advanced beside the anchors because both are per-frame facts and
    // anything time-based in the HUD should move at one rate. A skill's cooldown sweep counts
    // down off this — see `skillCooldown`.
    this.store.tickFrame();
  }
}
